import {NetatmoMetric} from '.';
import {DataGranularity, NetatmoDataGranularity} from '../../api/types';
import * as Dates from '../Dates';
import * as Time from '../Time';
import {CalendarAggregation} from '../Generic/types';

export const getDefaultGranularity = (scale: Time.Scale, metric: string) => {
  let granularity: NetatmoDataGranularity;
  switch (scale) {
    case Dates.DAY:
    case Dates.LAST_24_HOURS:
      granularity = metric === 'Rain' ? '1hour' : 'max';
      break;
    case Dates.WEEK:
    case Dates.LAST_7_DAYS:
      granularity = metric === 'Rain' ? '6hours' : '3hours';
      break;
    case Dates.MONTH:
    case Dates.LAST_30_DAYS:
      granularity = '1day';
      break;
    case Dates.YEAR:
      granularity = '1week';
      break;
    default:
      granularity = 'max';
  }
  return granularity;
};

export const formatGranularity = (
  granularity: NetatmoDataGranularity | DataGranularity,
) => {
  if (['max'].includes(granularity)) {
    return 'max';
  }
  if (['30min', '30MIN'].includes(granularity)) {
    return 'thirtyMinutes';
  }
  if (['1hour', 'H', '1H'].includes(granularity)) {
    return 'oneHour';
  }
  if (['3hours', '3H'].includes(granularity)) {
    return 'threeHours';
  }
  if (['6hours', '6H'].includes(granularity)) {
    return 'sixHours';
  }
  if (['1day', 'D', '1D'].includes(granularity)) {
    return 'oneDay';
  }
  if (['1week', 'W', '1W'].includes(granularity)) {
    return 'oneWeek';
  }
  if (['1month', 'M', '1M'].includes(granularity)) {
    return 'oneMonth';
  }
  return granularity;
};

export const getCalendarGranularityFromScale = (scale: Time.Scale) => {
  let granularity: NetatmoDataGranularity;
  switch (scale) {
    case Dates.DAY:
      granularity = '1hour';
      break;
    case Dates.WEEK:
      granularity = '1hour';
      break;
    case Dates.MONTH:
      granularity = '1day';
      break;
    case Dates.YEAR:
      granularity = '1day';
      break;
    default:
      granularity = '1day';
  }
  return granularity;
};

export const getDateFormatFromGranularity = (
  granularity: NetatmoDataGranularity,
) => {
  if (['max', '30min', '1hour', '3hours', '6hours'].includes(granularity)) {
    return '<b>%H:%M</b> (%A %e %B %Y)';
  }
  if (granularity === '1day') {
    return '%A %e %B %Y';
  }
  if (granularity === '1week') {
    return '%e %B %Y';
  }
  if (granularity === '1month') {
    return '%B %Y';
  }
};

export const getThermostatTypeListFromGranularity = (
  granularity: NetatmoDataGranularity,
) => {
  if (granularity === 'max') {
    return ['temperature', 'sp_temperature'];
  } else if (['30min', '1hour', '3hours'].includes(granularity)) {
    return ['temperature', 'sp_temperature'];
  } else {
    return ['temperature'];
  }
};

export const boilerIndependentSerie = (granularity: NetatmoDataGranularity) =>
  granularity === 'max';

export const getBoilerOnRatioFromScale = (
  value: number | null,
  granularity: NetatmoDataGranularity,
  timestamp: number,
) => {
  if (value === null) {
    return null;
  }

  if (granularity === '1hour') {
    return +((100 * value) / 3600).toFixed(0);
  }
  if (granularity === '3hours') {
    return +((100 * value) / (3 * 3600)).toFixed(0);
  }
  if (granularity === '6hours') {
    return +((100 * value) / (6 * 3600)).toFixed(0);
  }
  if (granularity === '1day') {
    return +((100 * value) / (24 * 3600)).toFixed(0);
  }
  if (granularity === '1week') {
    return +((100 * value) / (7 * 24 * 3600)).toFixed(0);
  }
  if (granularity === '1month') {
    const month = Dates.extractMonth(timestamp, 'utc');
    if (month === 2) {
      return +((100 * value) / (28 * 24 * 3600)).toFixed(0);
    }
    if ([1, 3, 5, 7, 8, 10, 12].includes(month)) {
      return +((100 * value) / (31 * 24 * 3600)).toFixed(0);
    }
    return +((100 * value) / (30 * 24 * 3600)).toFixed(0);
  }
};

export const getBoilerScaleFromGranularity = (
  granularity: NetatmoDataGranularity,
) => {
  if (['max', '30min'].includes(granularity)) {
    return '1hour';
  }
  return granularity;
};

export const getBoilerScaleFromGranularityAndPeriod = (
  granularity: NetatmoDataGranularity,
  scale: Time.Scale,
) => {
  if ([Dates.DAY, Dates.LAST_24_HOURS].includes(scale)) {
    if (['max', '30min'].includes(granularity)) {
      return '1hour';
    }
  } else if ([Dates.WEEK, Dates.LAST_7_DAYS].includes(scale)) {
    if (granularity === '1hour') {
      return '3hours';
    }
    if (granularity === '3hours') {
      return '6hours';
    }
  }
  return granularity;
};

export const getTypeListFromDataTypeAndGranularity = (
  dataType: string,
  granularity: NetatmoDataGranularity,
  // The Netatmo getmeasure API serves min_*/max_* at every aggregated
  // scale (30min and up); only raw 'max' scale rejects those filters.
  // Opt-in so chart payloads stay unchanged — the calendar view passes
  // true to get per-hour min/max on the day/week heatmaps.
  hourlyAggregations = false,
) => {
  const plainGranularities = hourlyAggregations
    ? ['max']
    : ['max', '30min', '1hour', '3hours'];
  const typeList = [] as string[];
  if (dataType === 'Rain') {
    if (granularity === 'max') {
      typeList.push('Rain');
    } else {
      typeList.push('sum_rain');
    }
  } else if (dataType === 'Humidity') {
    typeList.push('Humidity');
    if (!plainGranularities.includes(granularity)) {
      typeList.push('min_hum');
      typeList.push('max_hum');
    }
  } else if (dataType === 'Temperature') {
    typeList.push('Temperature');
    if (!plainGranularities.includes(granularity)) {
      typeList.push('min_temp');
      typeList.push('max_temp');
    }
  } else if (dataType === 'Wind') {
    typeList.push('WindStrength');
    typeList.push('GustStrength');
    typeList.push('WindAngle');
    typeList.push('GustAngle');
  } else {
    typeList.push(dataType);
  }

  return typeList;
};

/**
 * Whether the Netatmo calendar heatmap can offer an avg/min/max switch:
 * true when the positional array is [<metric>, min_*, max_*] — i.e.
 * Temperature/Humidity at '1day' (month/year calendar), plus '1hour'
 * (day/week calendar) when `hourlyAggregations` is set, since Netatmo
 * stations sample every ~5 minutes and the API aggregates per bucket.
 */
export const hasCalendarAggregations = (
  dataType: string,
  granularity: NetatmoDataGranularity,
  hourlyAggregations = false,
) => {
  const typeList = getTypeListFromDataTypeAndGranularity(
    dataType,
    granularity,
    hourlyAggregations,
  );
  return (
    typeList.length === 3 &&
    typeList[1].startsWith('min_') &&
    typeList[2].startsWith('max_')
  );
};

/**
 * The values the calendar's "displayed value" chip can offer, in display order.
 * The Netatmo Wind module always reports WindStrength and GustStrength together
 * — they are two channels of one device — so the switch is available at every
 * granularity and needs no per-station capability check, unlike the generic
 * side where a station may report FF without FXY.
 */
export const getCalendarValueChoices = (
  dataType: string,
  granularity: NetatmoDataGranularity,
  hourlyAggregations = false,
): CalendarAggregation[] => {
  if (dataType === 'Wind') {
    return ['avg', 'max'];
  }
  return hasCalendarAggregations(dataType, granularity, hourlyAggregations)
    ? ['avg', 'min', 'max']
    : [];
};

export const getMainTypeFromDataType = (dataType: string) => {
  const typeList = [] as string[];
  if (dataType === 'Rain') {
    typeList.push('sum_rain');
  } else if (dataType === 'Wind') {
    typeList.push('GustStrength');
  } else {
    typeList.push(dataType);
  }

  return typeList;
};

export const getPointFormat = (dataType: string) => {
  if (dataType === 'Wind') {
    return '<span style="color:{series.color}">\u25CF</span> {series.name}: <span class="tooltip-value"> <b>{point.y}</b> </span> ({point.z})<br>';
  }
  return '<span style="color:{series.color}">\u25CF</span> {series.name}: <span class="tooltip-value"> <b>{point.y}</b> </span><br>';
};

export const formatMetricLabel = (metric: NetatmoMetric) => {
  return metric.toLowerCase();
};

export const getDataTypeLabel = (dataType: string) => {
  return dataType;
};

export const getDataTypePrecision = (dataType: string) => {
  let precision: number;
  switch (dataType) {
    case 'Temperature':
      precision = 1;
      break;
    case 'Humidity':
      precision = 0;
      break;
    case 'Noise':
      precision = 0;
      break;
    case 'CO2':
      precision = 0;
      break;
    case 'Pressure':
      precision = 0;
      break;
    case 'Rain':
      precision = 1;
      break;
    default:
      precision = 0;
  }
  return precision;
};

export const getChartTypeFromDataType = (dataType: string) => {
  let chartType: string;
  switch (dataType) {
    case 'Temperature':
      chartType = 'spline';
      break;
    case 'Humidity':
      chartType = 'spline';
      break;
    case 'Noise':
      chartType = 'spline';
      break;
    case 'CO2':
      chartType = 'spline';
      break;
    case 'Pressure':
      chartType = 'spline';
      break;
    case 'Rain':
      chartType = 'column';
      break;
    case 'Wind':
      chartType = 'spline';
      break;
    default:
      chartType = 'spline';
  }
  return chartType;
};
